import { ColumnAnalysisResponse } from "@/modules/snapshots/businessReports.types";
import { BUSINESS_REPORTS_TAGS } from "@/modules/snapshots/businessReports.constraints";
import type { PivotConfig, PivotFieldSchema } from "@/modules/snapshots/grid/pivots/pivotTypes";
import { gridApi } from "./grid";

interface NormalisePivotFieldPayload {
  field: string;
  rows: Array<{ id: string; value: string }>;
}

interface NormalisePivotFieldResponse {
  mapping: Record<string, string>;
}

interface PivotFieldSchemaResponse {
  fields: PivotFieldSchema[];
}

interface SnapshotPivotResponse {
  id: string;
  title: string;
  subtitle: string;
  config: PivotConfig;
  order: number;
}

interface SnapshotPivotCreatePayload {
  snapshotCollectionId: string;
  title: string;
  subtitle: string;
  config: PivotConfig;
}

/** Pivots and column analysis over a snapshot collection. */
export const analysisApi = gridApi.injectEndpoints({
  overrideExisting: false,
  endpoints: builder => ({
    getPivotFieldSchema: builder.query<PivotFieldSchemaResponse, string>({
      providesTags: [BUSINESS_REPORTS_TAGS.PIVOT_FIELD_SCHEMA_TAG],
      query: snapshotCollectionId => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshotCollectionId}/pivots/field-schema`,
      }),
    }),
    getSnapshotPivots: builder.query<SnapshotPivotResponse[], string>({
      providesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_TAG],
      query: snapshotCollectionId => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshotCollectionId}/pivots`,
      }),
    }),
    createSnapshotPivot: builder.mutation<SnapshotPivotResponse, SnapshotPivotCreatePayload>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_TAG],
      query: ({ snapshotCollectionId, title, subtitle, config }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshotCollectionId}/pivots`,
        method: "POST",
        data: { title, subtitle, config },
      }),
    }),
    deleteSnapshotPivot: builder.mutation<void, { snapshotCollectionId: string; pivotId: string }>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_TAG],
      query: ({ snapshotCollectionId, pivotId }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshotCollectionId}/pivots/${pivotId}`,
        method: "DELETE",
      }),
    }),
    normalisePivotField: builder.mutation<NormalisePivotFieldResponse, NormalisePivotFieldPayload>({
      query: data => ({
        url: "/pf-mainweb/business-reports/pivots/normalise-field",
        method: "POST",
        data,
      }),
    }),
    generateColumnAnalysisPrompt: builder.mutation<
      void,
      {
        business_report_id: string;
        snapshot_collection_id: string;
        subcollection_id?: string | null;
        field_name: string;
        conversation_id: string;
        contract_family_projection?: boolean;
      }
    >({
      query: data => ({
        url: "/pf-mainweb/business-reports/column-analysis/generate-prompt",
        method: "POST",
        data,
      }),
    }),
    executeColumnAnalysis: builder.mutation<
      void,
      {
        business_report_id: string;
        snapshot_collection_id: string;
        subcollection_id?: string | null;
        field_name: string;
        prompt: string;
        conversation_id: string;
        contract_family_projection?: boolean;
      }
    >({
      query: data => ({
        url: "/pf-mainweb/business-reports/column-analysis/execute",
        method: "POST",
        data,
      }),
    }),
    columnAnalysisConversation: builder.mutation<
      void,
      {
        business_report_id: string;
        snapshot_collection_id: string;
        subcollection_id?: string | null;
        field_name: string;
        query: string;
        conversation_history: Array<{ role: "user" | "assistant"; content: string }>;
        conversation_id: string;
        contract_family_projection?: boolean;
      }
    >({
      query: data => ({
        url: "/pf-mainweb/business-reports/column-analysis/conversation",
        method: "POST",
        data,
      }),
    }),
    saveColumnAnalysis: builder.mutation<
      ColumnAnalysisResponse,
      {
        business_report_id: string;
        snapshot_collection_id: string;
        subcollection_id?: string | null;
        field_name: string;
        prompt: string;
        result: string;
        contract_family_projection?: boolean;
      }
    >({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.COLUMN_ANALYSIS_TAG],
      query: data => ({
        url: "/pf-mainweb/business-reports/column-analysis/save",
        method: "POST",
        data,
      }),
    }),
    getColumnAnalyses: builder.query<
      ColumnAnalysisResponse[],
      {
        snapshot_collection_id: string;
        subcollection_id?: string | null;
        field_name?: string;
        contract_family_projection?: boolean;
      }
    >({
      providesTags: [BUSINESS_REPORTS_TAGS.COLUMN_ANALYSIS_TAG],
      query: params => ({
        url: "/pf-mainweb/business-reports/column-analysis",
        params,
      }),
    }),
    deleteColumnAnalysis: builder.mutation<void, string>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.COLUMN_ANALYSIS_TAG],
      query: id => ({
        url: `/pf-mainweb/business-reports/column-analysis/${id}`,
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useGetPivotFieldSchemaQuery,
  useGetSnapshotPivotsQuery,
  useCreateSnapshotPivotMutation,
  useDeleteSnapshotPivotMutation,
  useNormalisePivotFieldMutation,
  useGenerateColumnAnalysisPromptMutation,
  useExecuteColumnAnalysisMutation,
  useColumnAnalysisConversationMutation,
  useSaveColumnAnalysisMutation,
  useGetColumnAnalysesQuery,
  useDeleteColumnAnalysisMutation,
} = analysisApi;
